import React, { useCallback, useContext, useEffect, useState } from "react";
import styles from './metro.module.scss';
import _ from "lodash";
import cx from "classnames";
import { BannerView } from "../../../../components/banner";
import { CollectionEditState } from "../../../../types/collections";
import { MenuState } from "../../components/menu";
import { useTransition, animated, useSpringRef, useChain, useSpring } from "@react-spring/web";
import { BannerEditor } from "../../components/banner/library";
import { BannerFilters } from "../../components/banner/filter";
import { GridLayout } from "./grid";
import { SectionBar } from "./sections";
import { CollectionContent } from "../../components/content";
import { CropEditor } from "../../components/crop";
import useMeasure from "react-use-measure";
import { CollectionDescription } from "../../components/description";
import { CustomerLogo } from "../../components/customerLogo";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SectionState } from "../../types";
import { fixCursorSnapOffset } from "src/services/dnd";
import { snapCenterToCursor } from "@dnd-kit/modifiers";
import { DraggingSectionCard } from "./sections";
import { CollectionName } from "../../components/name";
import { CollectionContext } from "../../components/context";

interface MetroExperienceProps {
  collection: CollectionEditState;
  menuState: MenuState;
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void;
}

const MetroExperience: React.FC<MetroExperienceProps> = ({ collection, menuState, onMenuChange }) => {
  const collectionContext = useContext(CollectionContext);

  const showAssets = menuState.primaryAction === 'view' || menuState.primaryAction === 'edit';
  const showBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === null;
  const cropBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'crop';
  const showFilters = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'filter';

  const banner = _.get(collection, 'banner');

  const [draggingSection, setDraggingSection] = useState<SectionState | null>(null);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const sensors = useSensors(
    pointerSensor
  );

  const [headerAnimation] = useSpring({
    opacity: showAssets ? 1 : 0,
  }, [showAssets]);

  const assetsRef = useSpringRef();
  const assetsTransitions = useTransition(showAssets, {
    ref: assetsRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
  });

  const bannerEditorRef = useSpringRef();
  const bannerEditorTransitions = useTransition(showBanner, {
    ref: bannerEditorRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const bannerFilterRef = useSpringRef();
  const bannerFilterTransitions = useTransition(showFilters, {
    ref: bannerFilterRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: {
      friction: 14,
      tension: 120,
    },
  });

  const cropperRef = useSpringRef();
  const cropperTransitions = useTransition(cropBanner, {
    ref: cropperRef,
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    delay: showBanner ? 0 : 0,
    config: {
      friction: 14,
      tension: 120,
    },
  });

  useChain([assetsRef, bannerEditorRef, bannerFilterRef, cropperRef], [0, 0, 0, 0, 0]);

  useEffect(() => {
    //transitionRef.start();
  }, [showAssets, showBanner]);

  const handleSectionDragStart = (event) => {
    const section = event.active.data.current.section;

    if (section) {
      setDraggingSection(section);
    }
  }

  const handleSectionDragEnd = () => {
    setDraggingSection(null);
  }

  return (
    <DndContext sensors={sensors}
      collisionDetection={fixCursorSnapOffset}
      modifiers={[]}
      onDragStart={handleSectionDragStart}
      onDragEnd={handleSectionDragEnd}
    >
      <div className={styles.layout}>
        <div className={cx(styles.header, styles[collectionContext.entityType], {
          [styles.editing]: menuState.primaryAction !== 'view',
        })}>
          <div className={styles.banner}>
            <BannerView banner={banner} defaultBanner="/images/collections/background.jpeg" />
          </div>
          <animated.div style={headerAnimation} className={styles.inner}>
            {menuState.primaryAction === 'edit' && collectionContext.entityType === 'conversation' &&
              <CollectionName collection={collection} className={styles.collectionName} />
            }
            <CustomerLogo className={styles.customerLogo}
              collection={collection}
              menuState={menuState}
              onMenuChange={onMenuChange}
            />

            <CollectionDescription
              collection={collection}
              menuState={menuState}
              onMenuChange={onMenuChange}
            />
          </animated.div>
        </div>
        <div className={cx(styles.body, {
          [styles.cropping]: cropBanner
        })}>
          {assetsTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <div className={styles.assets}>
                <CollectionContent
                  collection={collection}
                  sectionBar={SectionBar}
                  layout={GridLayout}
                  menuState={menuState}
                  onMenuChange={onMenuChange}
                />
              </div>
            </animated.div>
          ))}

          {bannerEditorTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <BannerEditor collection={collection} />
            </animated.div>
          ))}

          {bannerFilterTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <BannerFilters collection={collection} />
            </animated.div>
          ))}

          {cropperTransitions((style, item) => (
            item && <animated.div style={style} className={styles.content}>
              <CropEditor enabled={cropBanner}
                collection={collection}
                menuState={menuState}
                onMenuChange={onMenuChange}
              />
            </animated.div>
          ))}
        </div>
      </div>
      <DragOverlay modifiers={[snapCenterToCursor]} dropAnimation={null} adjustScale={false}>
        {draggingSection && (
          <DraggingSectionCard section={draggingSection} />
        )}
      </DragOverlay>
    </DndContext>
  );
}

export { MetroExperience };